import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { UnitSelector } from 'app/types/costAndFees.type';
import {
  CostsAndFeesContextProvider,
  useCostsAndFeesContext,
} from 'app/shared/modules/hdp/CostsAndFees/context/CostsAndFeesProvider';
import { hasFeesInSection } from 'app/shared/modules/hdp/CostsAndFees/utils';
import Card from '../../../../ui/Card';
import { SpecialOfferBanner } from '../../CostAndFeesModule/SpecialOfferBanner';
import { CostSummaryCard } from '../../CostAndFeesModule/CostSummaryCard';
import { FeeBreakdownCard } from '../../CostAndFeesModule/FeeBreakdownCard';
import { LeaseTermsInline } from '../../CostAndFeesModule/LeaseTermsInline';
import * as styles from '../../CostAndFeesModule/costAndFeesModule.styles';

type EstimateSectionInnerProps = {
  unitName: string;
  unitBeds: number;
};

const findMatchingUnitSelector = (units: Array<UnitSelector>, unitName: string, unitBeds: number) => {
  return units.find((u) => u.numBeds === unitBeds && u.selector.displayName?.includes(unitName));
};

const EstimateSectionInner = ({ unitName, unitBeds }: EstimateSectionInnerProps) => {
  const { calculatedCostsAndFees, setSelectorOverrides, setSelectedOptions } = useCostsAndFeesContext();
  const prevUnitNameRef = useRef(unitName);

  useEffect(() => {
    if (!calculatedCostsAndFees?.selectors?.units) return;

    const matchingUnit = findMatchingUnitSelector(calculatedCostsAndFees.selectors.units, unitName, unitBeds);

    if (matchingUnit) {
      setSelectorOverrides([matchingUnit.selector]);
    }

    if (prevUnitNameRef.current !== unitName) {
      setSelectedOptions({});
      prevUnitNameRef.current = unitName;
    }
  }, [unitName, unitBeds, calculatedCostsAndFees?.selectors?.units, setSelectorOverrides, setSelectedOptions]);

  if (!calculatedCostsAndFees) return null;

  const { fees, costs } = calculatedCostsAndFees;

  return (
    <>
      <CostSummaryCard />

      {fees?.monthly && hasFeesInSection(fees.monthly) && (
        <FeeBreakdownCard
          title="Monthly rent & fees"
          feeSection={fees.monthly}
          totalDisplayName={costs?.monthly?.displayName}
          totalAmount={costs?.monthly?.amount}
        />
      )}

      {fees?.moveIn && hasFeesInSection(fees.moveIn) && (
        <FeeBreakdownCard
          title="One-time fees"
          feeSection={fees.moveIn}
          totalDisplayName={costs?.moveIn?.displayName}
          totalAmount={costs?.moveIn?.amount}
        />
      )}
    </>
  );
};

type EstimateSectionProps = {
  unitName: string;
  unitBeds: number;
};

export const EstimateSection = ({ unitName, unitBeds }: EstimateSectionProps) => {
  const floorplans = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing?.floorplans);

  return (
    <section>
      <Card variant="default" size="md" title="Estimate your total">
        <div className={styles.subcardsContainer}>
          <SpecialOfferBanner />
          <CostsAndFeesContextProvider floorPlans={floorplans as never}>
            <EstimateSectionInner unitName={unitName} unitBeds={unitBeds} />
          </CostsAndFeesContextProvider>
          <LeaseTermsInline />
        </div>
      </Card>
    </section>
  );
};
